import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// Styles for the shared day × week intensity heat grid — lifted verbatim
// from apps/mobile previewStyles.ts (the v3.2.0 Home preview look) so the
// Home re-mount renders identically. Row = one ≥44pt tap target;
// selection is per-day, never per-cell.
// The week-label row's height, shared by the row itself and by the spacer that
// keeps the pinned day column in step with it. One constant, two consumers —
// see gridPinnedHeadSpacer for what happened when they were two numbers.
//
// ⭐ PE-29 — it is now one constant CHOSEN, not one constant fixed, because the
// header stopped being a label everywhere: where the editor can drop a week the
// head cell is a control (hold it to remove that week), and a control is bound
// by the 44pt floor. Home passes no remover, so its header stays 20 and its
// preview does not grow by 24px for a gesture it does not have.
// ⛔ Both consumers still read the SAME value — that is the invariant the shear
// of 28/08 bought; what changed is that the value has two cases, not that there
// are two numbers again.
const GRID_HEAD_H_LABEL = 20;
const GRID_HEAD_H_TAP = 44;

export function makeGridStyles(theme: UiTheme, headIsControl = false) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  const GRID_HEAD_H = headIsControl ? GRID_HEAD_H_TAP : GRID_HEAD_H_LABEL;
  return StyleSheet.create({
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    gridHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      marginBottom: s.s1,
    },
    // ⚠️ The head cell must share the CELLS' width rules exactly, or the
    // labels drift off their columns — the bench caught this rendering
    // "W1 W2 … W12" bunched at the left while the cells below were wide.
    // ⇒ The width rules live on the BOX (which is the Pressable where the week
    // can be removed), byte-identical to gridCellTouch's; the label only
    // centres inside it.
    gridWeekHeadCell: {
      minWidth: 44,
      flexGrow: 1,
      flexBasis: 0,
      height: GRID_HEAD_H,
      justifyContent: "center",
    },
    // PE-29 — "un po' troppo piccoli" (Antenore, smoke 28/08): xs/ink3 is the
    // caption treatment, and this row is the grid's other AXIS — it has to read
    // as a heading beside the day numbers, which are already sm/700.
    gridWeekHead: {
      textAlign: "center",
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    // #336 S2: the round type's glyph, centred under the W.
    gridWeekMark: { alignItems: "center" },
    // Baseline rows carry an invisible border with the same metrics as
    // the selected ring, so selecting never shifts the layout.
    gridRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      minHeight: 44,
      borderWidth: 2,
      borderColor: "transparent",
      borderRadius: r.r3,
      paddingHorizontal: s.s1,
    },
    // The selected day: surface tint, and the day number flips to brand — so
    // the row's selection is never colour-alone even without its ring.
    gridRowSelected: {
      backgroundColor: p.surface,
    },
    // ⛔ THE RING IS SPENT ON ONE THING AT A TIME. This file already says it,
    // about the placement mark: "a ringed cell inside a ringed row was two
    // nested rings of the same colour meaning two different things". Fusing the
    // two grids let that defect back in through the side door — the cursor is a
    // brand ring on a CELL, and it would sit inside the selected ROW's brand
    // ring. It never happened before because the ring lived in one grid and the
    // cursor in the other.
    // ⇒ When there IS a cursor (the editor), the ring belongs to the cursor and
    // the row keeps tint + brand number. With no cursor (Home, which passes no
    // selectedWeek) the ring is the row's, exactly as it has always been.
    gridRowSelectedRing: {
      borderColor: p.brand500,
    },
    gridRowPressed: { opacity: 0.6 },
    // ⛔ PE-27 — the selection ring must survive being CUT IN HALF. Pinning the
    // day column means one row is now two boxes, and ringing both drew two
    // rings with a gutter between them: on the bench it read as two separate
    // selections rather than one row. These join them back — the facing edges
    // go transparent and lose their corner radius, so at rest the ring reads as
    // one continuous frame, and when the body is scrolled the open edge is
    // exactly where the content continues.
    // ⚠️ The border stays 2px WIDE and only changes COLOUR: dropping the width
    // would shift the halves by 2px against each other, which is the shear this
    // grid has already paid for once.
    gridRowSelectedPinned: {
      borderRightColor: "transparent",
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    gridRowSelectedBody: {
      borderLeftColor: "transparent",
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    gridDayNum: {
      minWidth: 34,
      textAlign: "center",
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    // PE-29 — the selected day was brand ink on the row's tint and nothing
    // else, because in the editor the ring belongs to the cursor (see
    // gridRowSelectedRing). Antenore, smoke 28/08: "i giorni selezionati non si
    // vedono bene". A tint plus a same-hue number is two whispers; the filled
    // box is the treatment Chip already spends on `selected`
    // (primitives/Chip.tsx:165 — brand500 fill, inkOnBrand label), so the two
    // surfaces say "selected" the same way.
    // ⛔ Not colour-alone: the SHAPE changes (a flat number becomes a filled
    // box), which is what a reduced-vision reader picks up first, and the
    // pinned button already carries accessibilityState.selected.
    gridDayNumSelected: { color: p.inkOnBrand },
    // ⛔ Home is NOT the editor here. With no cursor the ring is the ROW's
    // (gridRowSelectedRing), and a filled box inside a ringed row is the same
    // "two nested marks of one colour meaning two things" this file already
    // refuses for the cursor. Home keeps the brand number it has always had;
    // the fill is what the editor spends INSTEAD of the ring it gave away.
    gridDayNumSelectedRing: { color: p.brand500 },
    // ⛔ THE PINNED HALF MUST HOLD A 44pt BOX, exactly like the body half does.
    // Measured, not guessed: the body row's content is a `gridCellTouch` at 44,
    // so with the row's 2px border its pitch is 48 — while the pinned half held
    // only a <Text> and settled at the row's own minHeight of 44. Four pixels a
    // row, which by day 7 is 24px of shear, and it is exactly what the bench
    // screenshot showed before this box existed.
    // ⇒ The invariant is STRUCTURAL, not arithmetic: both halves contain a
    // 44pt box, so neither can drift when the other's padding changes.
    // ⛔ And 44 on BOTH axes, not just height. Splitting the row into a pinned
    // half and a scrolling half turned the day label into its own small button,
    // ~36 wide — a narrow target that did not exist before, because the whole
    // row used to be the Pressable. It is the identical defect this fetta had
    // just fixed in ConfirmRemoveButton ("BOTH axes, not just height") and it
    // came straight back one file over: splitting a container is a way to
    // MANUFACTURE a small tap target, not just to move one.
    gridDayNumBox: {
      minWidth: 44,
      minHeight: 44,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: r.r2,
    },
    gridDayNumBoxSelected: { backgroundColor: p.brand500 },
    gridCells: {
      flex: 1,
      flexDirection: "row",
      gap: s.s1,
    },
    // ⭐ PE-27 — THE THRESHOLD THAT ISN'T A CONSTANT.
    // Until now this was `flex: 1` with no floor: the cells DIVIDED whatever
    // width was going, so more weeks meant narrower cells, without limit. That
    // is DS-63 — at 12 weeks on a 360dp phone the cell fell to ~22px and the
    // band glyph (absolutely positioned top-left) collided with the centred
    // value. Home renders this same grid, so the family already had it.
    //
    // `minWidth: 44` + `flexGrow: 1` inside a horizontal ScrollView whose
    // contentContainer also grows makes the behaviour switch BY ITSELF at
    // exactly the right point: while the weeks fit, flexGrow spreads them to
    // fill the width — byte-identical to the old `flex: 1` — and once they no
    // longer fit, the cells stop at 44 and the grid scrolls instead of
    // crushing. No onLayout, no measured breakpoint, no magic number to tune:
    // the only constant is 44, which is the a11y tap floor the standards
    // already impose, and which PE-27 makes binding here because the CELL is
    // now the tap target (it was the row).
    //
    // ⛔ Do not "simplify" this back to flex:1 — and note that the old
    // placement matrix could NOT afford this floor (placementMatrixStyles.ts
    // documented the bench measuring seven 44px day-columns overflowing 360dp
    // and riding over the − ). That was true in ITS orientation, where the
    // long axis was the one without room. Weeks-as-columns + scroll is what
    // buys the floor: the overflow becomes scrollable instead of destructive.
    //
    // ⚠️ THE TOUCH BOX IS NOT THE PAINTED BOX. The heat cell is 26px tall by
    // design (it is a glance, not a button) and stays so; the floor belongs to
    // the Pressable AROUND it, which is 44 in both axes. Putting `minHeight:
    // 44` on the painted cell would have grown every swatch on Home by 18px —
    // a visual change nobody asked for, to satisfy a rule about touch. And
    // hitSlop cannot stand in for it: react-native-web drops hitSlop entirely
    // (DS-48), so on the web mount the floor has to be real geometry.
    gridCellTouch: {
      minWidth: 44,
      minHeight: 44,
      flexGrow: 1,
      flexBasis: 0,
      justifyContent: "center",
    },
    gridCell: {
      alignSelf: "stretch",
      height: 26,
      borderRadius: r.r2,
      alignItems: "center",
      justifyContent: "center",
    },
    // Band glyph (W/M/H/X) in the cell's top-left corner — the non-colour cue
    // so the band never rides on fill colour alone, so it must READ: `xs` (11)
    // matching the legibility floor bumped across this grid, full opacity (it's
    // the safety cue, not decoration), colour set inline to the band's `on` ink.
    gridCellBand: {
      position: "absolute",
      top: 1,
      left: 3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
    },
    // Training prescribed but no banded intensity (kind none/history/kg):
    // outlined instead of filled, the set count as the cell text — a
    // "free" day must never read as a rest day.
    gridCellFree: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: p.border,
    },
    // PE-26 — the focused exercise's cells: a ✓ in the corner opposite the
    // band glyph, the SAME mark PlacementMatrix puts on a placed cell
    // (placementMatrixStyles cellGlyph). The two matrices then say the one
    // thing the one way, which is the whole point of syncing them.
    // ⛔ The first try was a brand ring, and the visual bench killed it: this
    // grid ALREADY spends a brand ring on gridRowSelected — the selected DAY —
    // so a ringed cell inside a ringed row was two nested rings of the same
    // colour meaning two different things. The corner is free; the ring was not.
    // Colour is set inline to the cell's own `on` ink (the contrast the band
    // glyph and the value already clear), so this never rides on colour alone
    // and never picks a tone the fill can swallow.
    gridCellLit: {
      position: "absolute",
      top: 1,
      right: 3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
    },
    gridCellNum: {
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    gridCellFreeNum: { color: p.ink2 },
    // PE-27 — the cursor: the ONE cell the editor's other surfaces are talking
    // about (roster, card, week deck). Only painted when a week is selected,
    // which Home never does, so Home never grows a second ring.
    // ⛔ It is a ring, not a fill: the fill already means intensity band and a
    // second meaning on the same channel would make the heat unreadable. Same
    // reason placementMatrixStyles kept the ring for the cursor alone.
    gridCellCursor: {
      borderWidth: 2,
      borderColor: p.brand500,
    },
    gridCellPressed: { opacity: 0.6 },
    // The pinned day column and the scrolling body are two siblings in a row.
    // ⭐ ONE ScrollView, not two synchronised ones: the week header lives
    // INSIDE the scroller together with the cell rows, so there is no scroll
    // offset to keep in step and no chance of the header drifting from its
    // columns. The pinned column is simply outside it.
    gridFrame: { flexDirection: "row" },
    gridPinnedCol: { justifyContent: "flex-start" },
    // ⛔ Reserves EXACTLY the height of the week-head row, so the pinned day
    // labels line up with their rows. A mismatch here shears the whole grid,
    // and that is not hypothetical: the first cut put the week − inside the
    // header, which made it 44 tall against this 20, and the bench showed
    // every day label sitting one row above its cells.
    // ⇒ The header is now LABELS ONLY and both heights come from one constant,
    // so the two cannot drift apart again. The week − moved to the structure
    // row, where it acts on the SELECTED week — symmetric with the day −, and
    // with a cursor on screen "the week you point at" IS the selected one.
    gridHeadH: { height: GRID_HEAD_H },
    gridPinnedHeadSpacer: { height: GRID_HEAD_H, marginBottom: s.s1 },
    gridScrollContent: { flexGrow: 1 },
    // PE-29 — structural handles: two ＋ and a sentence. The two removals used
    // to live here as armed − buttons naming their target ("− W1", "− D3");
    // Antenore's word on the 28/08 smoke was "bruttissimi", and they were also
    // the odd gesture out — the grid's own placement layer has taught hold-to-
    // act since PE-22 F. So removal moved ONTO the thing removed (hold a week
    // head, hold a day button) and this row keeps only what adds.
    gridStructureRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      marginTop: s.s2,
      flexWrap: "wrap",
    },
    // The hold is invisible until someone tries it, so it is written where the
    // ＋ are — the same slot, and the same voice, as gridHint below.
    // `flexShrink` so a long translation wraps instead of pushing the ＋ off.
    gridStructureHint: {
      flexShrink: 1,
      color: p.ink3,
      fontSize: t.sizes.xs,
    },
    gridHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      marginTop: s.s1,
    },
    // Spacing only — the swatch/glyph/label layout lives in <ScaleLegend> now.
    legendRow: { marginTop: s.s3 },
  });
}
